import { isString } from "es-toolkit";

/**
 * The entity that members are being invited to. It drives the helper messages, and everything but
 * the workspace itself also invites the new emails to the workspace as a side effect.
 */

/**
 * Returns whether the invite/create action should be disabled.
 * The action is disabled when there are no selected members,
 * the current input is invalid, or the selection exceeds the backend limit.
 */
export function shouldDisableSendInvite(selectedMembers, isMemberInputValid, maxInvitedMembers) {
  return !selectedMembers.length || !isMemberInputValid || !!maxInvitedMembers && selectedMembers.length > maxInvitedMembers;
}

/**
 * Returns the identity/email value for each selected autocomplete option.
 * New emails are plain strings, while selected members/teams are option objects
 * whose `value` holds the member identity or team id.
 */
export function getSelectedOptionValues(options) {
  return options.map(option => isString(option) ? option : option.value);
}

/**
 * Validates selected member identities/emails and returns
 * both UI feedback text and a validity flag.
 *
 * This is the selection-level validation that should be derived from the
 * currently selected members on every render (instead of being stored in
 * state). Deriving it ensures the message stays correct even when the
 * autocomplete fires an input-change event right after a paste, which would
 * otherwise clear the freshly computed duplicates/new-email message.
 */
export function validateInviteMembers({
  entityName,
  selectedOptions,
  companyMemberIds,
  maxInvitedMembers,
  canInviteNewEmails = true
}) {
  if (maxInvitedMembers && selectedOptions.length > maxInvitedMembers) {
    return {
      message: {
        type: "error",
        text: `Please reduce your selection to a maximum of ${maxInvitedMembers} members per invite request.`
      },
      isValid: false
    };
  }
  const selectedValues = getSelectedOptionValues(selectedOptions);
  const uniqueSelectedValues = new Set(selectedValues);

  // Duplicates are kept in the selection (e.g. when pasting a list) but only
  // one invitation is sent per email, so warn the user without blocking the
  // invite.
  if (uniqueSelectedValues.size !== selectedOptions.length) {
    return {
      message: {
        type: "warning",
        text: "Some of the emails you entered are duplicates and will be invited once"
      },
      isValid: true
    };
  }
  const companyMemberIdsSet = new Set(companyMemberIds);

  // Checking if any of the selected members do not exist in the company member
  // list, which means there are new emails that will be invited to the workspace.
  const hasNewEmails = selectedValues.some(value => !companyMemberIdsSet.has(value));
  if (canInviteNewEmails && entityName !== "company" && hasNewEmails) {
    return {
      message: {
        type: "info",
        text: "Inviting members by email will also invite them to the workspace"
      },
      isValid: true
    };
  }
  return {
    message: undefined,
    isValid: true
  };
}